// Master concerns data types and interfaces

// Master concern definition from datacenter
export interface MasterConcern {
  mvarid: string;           // Unique concern identifier (reusing mvarid naming for consistency)
  domain: 'parent' | 'provider' | 'system'; // Concern domain categories
  itemage: number;          // Age in months when concern is relevant
  domainseqnum: number;     // Sequence number within domain for ordering
  severity: 'low' | 'medium' | 'high' | 'critical'; // Concern severity level
  category: string;         // Sub-category within domain (e.g., 'behavior', 'development', 'health')
  lastUpdated: string;      // Server timestamp for sync management
  version: number;          // Version number for conflict resolution
}

// Concern text content (multilingual)
export interface ConcernText {
  mvarid: string;           // Links to MasterConcern.mvarid
  languagetype: string;     // Language code: 'en', 'es', 'fr', etc.
  htmltext: string;         // Rich text content (HTML formatted)
  shortText: string;        // Brief description for lists
  actionText?: string;      // Recommended action text
  lastUpdated: string;      // Server timestamp for sync management
  version: number;          // Version number for conflict resolution
}

// Combined concern with text for UI consumption
export interface ConcernWithText extends MasterConcern {
  text: string;             // Resolved text for current language
  shortText: string;        // Brief description
  actionText?: string;      // Recommended action
  isAvailable: boolean;     // Whether text is available in current language
}

// Master concerns storage structure
export interface MasterConcernData {
  concerns: MasterConcern[];
  concernTexts: ConcernText[];
  languages: ConcernLanguageConfig[];
  lastSyncTimestamp: string;
  dataVersion: string;      // Overall data version for bulk updates
}

// Language support configuration for concerns
export interface ConcernLanguageConfig {
  code: string;             // ISO language code
  name: string;             // Display name
  isDefault: boolean;       // Whether this is the default language
  isSupported: boolean;     // Whether this language is fully supported
}

// Indexed access structures for performance
export interface ConcernIndexes {
  byMvarid: Map<string, MasterConcern>;
  byDomain: Map<string, MasterConcern[]>;
  textByMvarid: Map<string, Map<string, ConcernText>>; // mvarid -> language -> text
  sequenceByDomain: Map<string, MasterConcern[]>; // domain -> sorted by domainseqnum
  bySeverity: Map<string, MasterConcern[]>; // severity -> concerns
  byCategory: Map<string, MasterConcern[]>; // category -> concerns
}

// Concern query and filtering
export interface ConcernQuery {
  domain?: 'parent' | 'provider' | 'system';
  minAge?: number;
  maxAge?: number;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  category?: string;
  language?: string;
  limit?: number;
  offset?: number;
}

// Concern sync metadata
export interface ConcernSyncMetadata {
  totalConcerns: number;
  totalTexts: number;
  supportedLanguages: string[];
  lastFullSync: string;
  incrementalSyncAvailable: boolean;
  domainBreakdown: {
    parent: number;
    provider: number;
    system: number;
  };
  severityBreakdown: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  dataIntegrity: {
    concernsWithoutText: string[];  // mvarids missing text
    orphanedTexts: string[];        // texts without corresponding concerns
    duplicateSequences: Array<{     // domain sequence conflicts
      domain: string;
      duplicatedSeqNums: number[];
    }>;
  };
}

// Storage and retrieval interfaces
export interface MasterConcernStorage {
  // Data management
  loadMasterData(): Promise<MasterConcernData | null>;
  saveMasterData(data: MasterConcernData): Promise<void>;
  updateConcerns(concerns: MasterConcern[]): Promise<void>;
  updateConcernTexts(texts: ConcernText[]): Promise<void>;
  
  // Query methods
  getConcernsByDomain(domain: 'parent' | 'provider' | 'system', language?: string): Promise<ConcernWithText[]>;
  getConcernByMvarid(mvarid: string, language?: string): Promise<ConcernWithText | null>;
  queryConcerns(query: ConcernQuery): Promise<ConcernWithText[]>;
  getConcernsBySeverity(severity: 'low' | 'medium' | 'high' | 'critical', language?: string): Promise<ConcernWithText[]>;
  
  // Index management
  buildIndexes(): Promise<ConcernIndexes>;
  validateDataIntegrity(): Promise<ConcernSyncMetadata>;
  
  // Sync support
  getDataVersion(): Promise<string>;
  getSyncMetadata(): Promise<ConcernSyncMetadata>;
  
  // Cache management
  clearCache(): Promise<void>;
  preloadForDomain(domain: 'parent' | 'provider' | 'system'): Promise<void>;
}

// Performance optimization constants
export const CONCERN_STORAGE_CONFIG = {
  // Storage keys
  MASTER_DATA_KEY: 'masterConcerns',
  CACHE_KEY_PREFIX: 'concern_cache_',
  INDEX_KEY: 'concern_indexes',
  
  // Performance settings
  MAX_CACHE_SIZE: 30, // Cache up to 30 concerns in memory
  INDEX_REBUILD_INTERVAL: 24 * 60 * 60 * 1000, // 24 hours
  PRELOAD_DOMAINS: ['parent'], // Always preload parent concerns
  
  // Language settings
  DEFAULT_LANGUAGE: 'en',
  FALLBACK_LANGUAGES: ['en'], // Fallback chain if requested language unavailable
  
  // Sync settings
  BATCH_SIZE: 15, // Process concerns in batches of 15
  MAX_RETRY_ATTEMPTS: 3,
  SYNC_TIMEOUT: 30000, // 30 seconds
  
  // Domain settings
  DOMAINS: ['parent', 'provider', 'system'] as const,
  SEVERITY_LEVELS: ['low', 'medium', 'high', 'critical'] as const,
};

// Utility type guards
export const isMasterConcern = (obj: any): obj is MasterConcern => {
  return obj && 
    typeof obj.mvarid === 'string' &&
    typeof obj.domain === 'string' &&
    ['parent', 'provider', 'system'].includes(obj.domain) &&
    typeof obj.itemage === 'number' &&
    typeof obj.domainseqnum === 'number' &&
    typeof obj.severity === 'string' &&
    ['low', 'medium', 'high', 'critical'].includes(obj.severity);
};

export const isConcernText = (obj: any): obj is ConcernText => {
  return obj &&
    typeof obj.mvarid === 'string' &&
    typeof obj.languagetype === 'string' &&
    typeof obj.htmltext === 'string' &&
    typeof obj.shortText === 'string';
};

// Data validation utilities
export const validateMasterConcern = (concern: MasterConcern): string[] => {
  const errors: string[] = [];
  
  if (!concern.mvarid?.trim()) errors.push('mvarid is required');
  if (!concern.domain?.trim()) errors.push('domain is required');
  if (!CONCERN_STORAGE_CONFIG.DOMAINS.includes(concern.domain as any)) {
    errors.push(`domain must be one of: ${CONCERN_STORAGE_CONFIG.DOMAINS.join(', ')}`);
  }
  if (concern.itemage < 0 || concern.itemage > 300) errors.push('itemage must be between 0-300 months');
  if (concern.domainseqnum < 0) errors.push('domainseqnum must be non-negative');
  if (!CONCERN_STORAGE_CONFIG.SEVERITY_LEVELS.includes(concern.severity as any)) {
    errors.push(`severity must be one of: ${CONCERN_STORAGE_CONFIG.SEVERITY_LEVELS.join(', ')}`);
  }
  if (!concern.category?.trim()) errors.push('category is required');
  
  return errors;
};

export const validateConcernText = (text: ConcernText): string[] => {
  const errors: string[] = [];
  
  if (!text.mvarid?.trim()) errors.push('mvarid is required');
  if (!text.languagetype?.trim()) errors.push('languagetype is required');
  if (!text.htmltext?.trim()) errors.push('htmltext is required');
  if (!text.shortText?.trim()) errors.push('shortText is required');
  if (text.htmltext.length > 10000) errors.push('htmltext too long (max 10000 chars)');
  if (text.shortText.length > 200) errors.push('shortText too long (max 200 chars)');
  if (text.actionText && text.actionText.length > 500) errors.push('actionText too long (max 500 chars)');
  
  return errors;
};

// ─── Stored concern data (sourced from AdminSegment 11272) ───────────────────

/** Single selectable concern option (one answer choice from a multiselect question) */
export interface StoredConcernItem {
  keyValue: string;   // Answer key/ID from backend
  dataValue: string;  // Answer data value
  text: string;       // Display text (aText from htmlAnswer)
  /** Full compound mvarName used in AppProfileDto MVarDto (e.g. "cn_adhd_3").
   *  For AdminSegment 11272 concerns: group.mvarName.toLowerCase() + '_' + keyValue.
   *  For Measures: the underlying measure mvarName.
   *  Optional for backward-compat with data stored before this field was added. */
  mvarName?: string;
}

/** One question from AdminSegment 11272 – represents all concerns for one domain */
export interface StoredConcernGroup {
  mvarName: string;       // Question MvarName (domain identifier)
  pageNum: string;        // Page number for ordering
  questionText: string;   // Question text (optional header label)
  domainKey: string;      // Derived tab key: 'parent'|'family'|'provider'|'measures'|'other'
  items: StoredConcernItem[];
}

/** Root object stored at CONCERN_STORAGE_KEY after extracting AdminSegment 11272 */
export interface StoredConcernData {
  segmentId: string;
  segmentTitle: string;
  extractedAt: string;
  languageId: string;
  groups: StoredConcernGroup[];
}

export const CONCERN_SEGMENT_ID = '11272';
export const CONCERN_STORAGE_KEY = 'concerns_data_11272';

/**
 * Explicit mvarName→domain mapping for AdminSegment 11272 concern questions.
 *
 * Each entry is a base multiselect mvar (mvarTypeId=2) and the UI domain tab
 * it belongs to.  The mapping is case-insensitive.
 *
 * Provider : CN_Medical, CN_MedicalHistory
 * Parental : CN_Behavior, CN_Experience
 * Family   : CN_Family
 */
const CONCERN_MVAR_DOMAIN_MAP: Record<string, string> = {
  cn_medical:       'provider',
  cn_medicalhistory:'provider',
  cn_behavior:      'parent',
  cn_experience:    'family',
  cn_family:        'family',
  cn_adhd:          'parent',
};

export const deriveConcernDomain = (mvarName: string): string => {
  const key = mvarName.toLowerCase();
  if (CONCERN_MVAR_DOMAIN_MAP[key]) return CONCERN_MVAR_DOMAIN_MAP[key];
  // Fallback heuristics for any future additions
  if (key.includes('medical') || key.includes('provider') || key.includes('clinic')) return 'provider';
  if (key.includes('behav') || key.includes('parent') || key.includes('adhd') || key.includes('hyper')) return 'parent';
  if (key.includes('fam') || key.includes('exper')) return 'family';
  if (key.includes('obs') || key.includes('meas')) return 'measures';
  return 'other';
};

// ─── Domain helper types and functions ───────────────────────────────────────

export type ConcernDomain = 'parent' | 'family' | 'provider' | 'measures';

export interface ConcernDomainInfo {
  key: ConcernDomain;
  name: string;
  description: string;
  icon: string;
}

// Get concern domain information for UI
export const getConcernDomains = (): ConcernDomainInfo[] => [
  {
    key: 'parent',
    name: 'Behavior',
    description: 'Concerns about your child\'s behavior',
    icon: '👨‍👩‍👧‍👦',
  },
  {
    key: 'family',
    name: 'Family',
    description: 'Concerns shared by the broader family unit',
    icon: '🏠',
  },
  {
    key: 'provider',
    name: 'Medical',
    description: 'Clinical concerns and medical observations',
    icon: '🩺',
  },
  {
    key: 'measures',
    name: 'Measures',
    description: 'Concerns about measures results',
    icon: '📊',
  },
];